import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/commerce-php/commerce-php/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "code-changes"
    }}>{`Code changes`}</h1>
    <p>{`Every release includes a change in the codebase. The scope of the change determines whether the MAJOR, MINOR, or PATCH number increases in the version.`}</p>
    <h2 {...{
      "id": "public-vs-private-code-changes"
    }}>{`Public vs private code changes`}</h2>
    <p>{`A module's codebase consists of public and private code.
Public code includes Public API(indicated by the `}<inlineCode parentName="p">{`@api`}</inlineCode>{` docblock tag) and Public Customization Points.
All other code is considered private.`}</p>
    <p>{`Changes in public code always trigger MINOR or MAJOR version increases.`}</p>
    <p>{`In most cases, modifications to private code will trigger PATCH version increases.
On rare occasions, if the Adobe development team made significant modifications to private code, they will increase the MINOR or MAJOR version.`}</p>
    <p>{`Third-party modules should not use private code.
If this is unavoidable, you must depend on the PATCH version of the core modules used.`}</p>
    <h2 {...{
      "id": "api-and-customization-points"
    }}>{`API and customization points`}</h2>
    <p>{`Modules call APIs to create new application scenarios.
Modifications that break the API will trigger an increase in a module's MAJOR version.`}</p>
    <p>{`Modules use Customization Points to customize existing application scenarios.
Modifications that break Customization Points will trigger an increase in a module's MINOR version.`}</p>
    <p>{`An interface represents either an API or a Customization Point.`}</p>
    <p>{`This policy allows third-party module developers to declare more granular dependencies depending on their module's interaction with core modules.`}</p>
    <h2 {...{
      "id": "deprecation"
    }}>{`Deprecation`}</h2>
    <p>{`Marking public code with `}<inlineCode parentName="p">{`@deprecated`}</inlineCode>{` indicates that Adobe plans to remove that code in a future MINOR release or potentially after a year if no MINOR version is released.`}</p>
    <p>{`When Adobe deprecates the API or a customization point in favor of a new implementation, the `}<inlineCode parentName="p">{`@see`}</inlineCode>{` annotation points to the new implementation.`}</p>
    <p><strong parentName="p">{`Deprecated Code Example:`}</strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`
/**
 * @deprecated since 2.1.0
 * @see \\Magento\\Framework\\Model\\ResourceModel\\Db\\AbstractDb::save()
 */
public function save()
{
    // ...
}
`}</code></pre>
    <h2 {...{
      "id": "version-increase-scenarios"
    }}>{`Version increase scenarios`}</h2>
    <p>{`This table lists code change scenarios and which version number it affects.
Use this table to understand what changes Adobe can make and which version number gets increased for that change.
Any change not listed below is considered a PATCH level change.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`API/Customization Point`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Code Change`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Version Change`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`PHP Interface`}</strong>{` (marked with `}<inlineCode parentName="td">{`@api`}</inlineCode>{`)`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`New interface`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MINOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`New method added`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MINOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Interface removed`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MAJOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Method removed`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MAJOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`New required method argument`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MAJOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`New optional method argument`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MAJOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Removed the last argument for a method`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MINOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Changed a method signature (excluding last argument removal)`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MAJOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`New method exception (excluding subtypes of existing exceptions)`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MAJOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`New method exception (subtypes of an existing one)`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`PATCH`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`PHP Class`}</strong>{` (marked with `}<inlineCode parentName="td">{`@api`}</inlineCode>{`)`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`New Class`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MINOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`New method added`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MINOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Class removed`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MAJOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Method removed`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MAJOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`New required method argument`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MAJOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`New optional method argument`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MINOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Removed a non-last argument`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MAJOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`New required constructor object argument`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MINOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`New optional constructor argument in a class `}<a parentName="td" {...{
              "href": "#php-classes-intended-for-extension"
            }}>{`intended for extension`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MINOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`New optional constructor argument in a class not `}<a parentName="td" {...{
              "href": "#php-classes-intended-for-extension"
            }}>{`intended for extension`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`PATCH`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`New required constructor scalar argument (without pre-configured value)`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MAJOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Removed a non-last constructor argument`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MAJOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Removed a last constructor argument`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`PATCH`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Changed format of the returned method result`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MAJOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`New method exception (excluding subtypes of existing exceptions)`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MAJOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`New method exception (subtypes of an existing one)`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`PATCH`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`JavaScript Class`}</strong>{` (marked with `}<inlineCode parentName="td">{`@api`}</inlineCode>{`)`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`New class`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MINOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`New method added`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MINOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`New argument added`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MINOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Class removed`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MAJOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Method removed`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MAJOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Argument removed`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MAJOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`New event`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MINOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Renamed event`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MAJOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Removed event`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MAJOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`New event property`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MINOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Changed event property`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MAJOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Removed event property`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MAJOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Changed event ordering`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MAJOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`JavaScript Modules (AMD Modules)`}</strong>{` (marked with `}<inlineCode parentName="td">{`@api`}</inlineCode>{`)`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`New module`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MINOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`New method added`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MINOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`New argument added`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MINOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Module removed`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MAJOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Method removed`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MAJOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Argument removed`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MAJOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`New event`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MINOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Renamed event`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MAJOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Removed event`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MAJOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`New event property`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MINOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Changed event property`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MAJOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Removed event property`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MAJOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Changed event ordering`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MAJOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`Virtual Type (marked with `}<inlineCode parentName="strong">{`@api`}</inlineCode>{`)`}</strong></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Virtual type removed`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MAJOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Virtual type added`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MINOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`URL Paths (Store-Front and Admin, GET)`}</strong></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Path removed`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MAJOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Removed/renamed a request parameter`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MAJOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`New required request parameter`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MAJOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`New optional request parameter`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MINOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`Console commands and their arguments`}</strong></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Command removed`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MAJOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`New required argument`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MAJOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`New optional argument`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MINOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Removed/renamed argument`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MAJOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`New command exit code`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MINOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`New command`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MINOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`Less variables and mixins`}</strong></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Removed variable`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MAJOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Removed mixin`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MAJOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`New required mixin argument`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MAJOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`Message queue topics and data types`}</strong></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Topic removed`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MAJOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Topic arguments modified`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MAJOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Consumer removed`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MINOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`New topic published`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MINOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`Layout handles declared by modules`}</strong></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`New layout page handle`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MINOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`New container/block added to handle`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MINOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Removed/renamed container/block`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MAJOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Removed layout handle`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MAJOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`Static and dynamic events triggered by a component`}</strong></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Event argument removed`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MAJOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Event removed`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MAJOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`Schema of configuration types introduced by module`}</strong></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Schema file or configuration type renamed/removed`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MAJOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Obligatory node/attribute added`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MAJOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Node/attribute removed`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MAJOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`New optional node/attribute added`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MINOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`Structure of System Configuration fields used by module`}</strong></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Config path added`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MINOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Config path removed/renamed`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MAJOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`Database structure`}</strong></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Table removed`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MAJOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Table added`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MINOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Column removed`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MAJOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Column added`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MINOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Compatible changes in column configuration (soften column constraints: increase size, make optional)`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`PATCH`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Incompatible changes in column configuration`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MAJOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Primary key column added/removed`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MAJOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Added column to unique key`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MAJOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Removed column from unique key`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MAJOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Unique key added/removed`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MAJOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Index added/changed`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`PATCH`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Foreign key added`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MAJOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Temporary tables added/removed/changed`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`PATCH`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`Static view files (marked with `}<inlineCode parentName="strong">{`@api`}</inlineCode>{`)`}</strong></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`JS/CSS/LESS file removed/moved`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MAJOR`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`JS/CSS/LESS file added`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`PATCH`}</td>
        </tr>
      </tbody>
    </table>
    <h3 {...{
      "id": "php-classes-intended-for-extension"
    }}>{`PHP Classes Intended for Extension`}</h3>
    <p>{`Though inheritance is discouraged, the following classes are still acceptable for extensions at the moment:`}</p>
    <ul>
      <li parentName="ul">{`\\Magento\\Framework\\Model\\AbstractExtensibleModel`}</li>
      <li parentName="ul">{`\\Magento\\Framework\\Api\\AbstractExtensibleObject`}</li>
      <li parentName="ul">{`\\Magento\\Framework\\Api\\AbstractSimpleObject`}</li>
      <li parentName="ul">{`\\Magento\\Framework\\Model\\AbstractModel`}</li>
      <li parentName="ul">{`\\Magento\\Framework\\App\\Action\\Action`}</li>
      <li parentName="ul">{`\\Magento\\Backend\\App\\Action`}</li>
      <li parentName="ul">{`\\Magento\\Backend\\App\\AbstractAction`}</li>
      <li parentName="ul">{`\\Magento\\Framework\\App\\Action\\AbstractAction`}</li>
      <li parentName="ul">{`\\Magento\\Framework\\View\\Element\\AbstractBlock`}</li>
      <li parentName="ul">{`\\Magento\\Framework\\View\\Element\\Template`}</li>
    </ul>
    <p>{`As the framework improves, this list should be reduced and, eventually, eliminated.`}</p>
    <p><strong parentName="p">{`Related topics:`}</strong></p>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://experienceleague.adobe.com/en/docs/commerce-operations/release/planning/versioning-policy"
        }}>{`Release policy`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://experienceleague.adobe.com/en/docs/commerce-operations/release/planning/schedule"
        }}>{`Upcoming releases`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://developer.adobe.com/commerce/contributor/guides/code-contributions/backward-compatibility-policy/"
        }}>{`Backward compatibility policy`}</a></li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      